import type { RenderMode, SduiNode } from "@ethisyscore/protocol";

/**
 * Configuration accepted by {@link defineDeclarativePlugin}.
 *
 * A Contract A (host-rendered) plugin contributes a map of resource URIs to
 * declarative SDUI trees. The host fetches a resource by URI and renders it
 * against the v1 vocabulary.
 */
export interface DeclarativePluginConfig
{
    resources: Record<string, SduiNode>;
}

/**
 * Configuration accepted by {@link defineEthisysPlugin}.
 *
 * `renderMode` is constrained to the protocol's {@link RenderMode} enum so
 * authoring mistakes (`"iframe"`, `"webview"`, …) are caught at compile time.
 * `mount` is generic so Contract B remote-runtime authors can attach their
 * own mount surface without losing type information at the call site.
 */
export interface EthisysPluginConfig<TMount>
{
    renderMode: RenderMode;
    mount?: TMount;
}

/**
 * Author-facing identity helper for a Contract A (host-rendered) declarative
 * plugin definition.
 *
 * The helper performs no runtime work — it exists purely so authoring sites
 * receive precise type inference and editor tooling against the protocol's
 * {@link SduiNode} contract. The returned value is the exact same reference
 * the caller passed in.
 */
export const defineDeclarativePlugin = (
    cfg: DeclarativePluginConfig,
): DeclarativePluginConfig => cfg;

/**
 * Author-facing identity helper for a generic EthisysCore plugin definition.
 *
 * The helper performs no runtime work. It constrains `renderMode` to the
 * protocol's {@link RenderMode} enum and preserves the inferred type of an
 * optional `mount` surface, so Contract B authors can pass through their own
 * mount object without widening it to `unknown`.
 */
export const defineEthisysPlugin = <TMount>(
    cfg: EthisysPluginConfig<TMount>,
): EthisysPluginConfig<TMount> => cfg;
